<template>
  <v-select
    class="custom-select"
    :class="{ 'is-invalid': !!errors?.length }"
    v-bind="$attrs"
    :value="value"
    :options="options"
    :placeholder="placeholder"
    :clearable="false"
    :searchable="false"
    :reduce="(option: string | number) => option"
    :get-option-label="(option: string | number) => option.toString()"
    @input="handleInput"
    @focusout="emitBlur"
    @open="emitOpen"
  />
</template>

<script setup lang="ts">
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { useField } from 'vee-validate';

/**
 * コンポーネントのプロパティ定義
 * @property {Array} options - 選択肢の配列
 * @property {string} placeholder - プレースホルダーテキスト
 * @property {Array} errors - エラーメッセージの配列
 * @property {string|number} value - 現在選択されている値
 * @property {string} name - vee-validateのフィールド名
 */
const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  placeholder: {
    type: String,
    default: '選択してください',
  },
  errors: {
    type: Array,
    default: () => [],
  },
  value: {
    type: [String, Number],
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
});

/**
 * イベントの定義
 * @event input - 値が変更された時に発火
 * @event blur - フォーカスが外れた時に発火
 * @event open - プルダウンメニューが開いた時に発火
 */
const emit = defineEmits(['input', 'blur', 'open']);

/**
 * vee-validateのフィールドハンドラーを取得
 */
const { handleChange } = useField(props.name);

/**
 * 入力値が変更された時のハンドラー
 * @param {any} value - 新しい値
 */
const handleInput = (value: any): void => {
  // vee-validateのフィールド値を更新
  handleChange(value);
  // 親コンポーネントに変更を通知
  emit('input', value);
};

/**
 * フォーカスが外れた時のハンドラー
 */
const emitBlur = (): void => {
  emit('blur');
};

/**
 * プルダウンメニューが開いた時のハンドラー
 */
const emitOpen = (): void => {
  emit('open');
};
</script>

<style scoped>
/* カスタムセレクトボックスの基本スタイル */
.custom-select {
  height: 46px;
}

/* ドロップダウンメニューのスタイル */
.custom-select :deep(.vs__dropdown-menu) {
  max-height: 200px;
  overflow-y: auto;
}

/* 選択済みの値のスタイル */
.custom-select :deep(.vs__selected) {
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 14px;
  opacity: 1 !important;
}

/* 検索ボックスのスタイル */
.custom-select :deep(.vs__search) {
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 14px;
}

/* ドロップダウントグルボタンのスタイル */
.custom-select :deep(.vs__dropdown-toggle) {
  border: var(--bs-border-width) solid var(--bs-border-color);
  background: var(--bs-silver-primary);
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 14px;
  height: 46px;
  display: flex;
  align-items: center;
}

/* ドロップダウンが開いている時のスタイル */
.custom-select :deep(.vs--open .vs__dropdown-toggle) {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* 選択済みのオプションのスタイル */
.custom-select :deep(.vs__selected-options) {
  padding: 0;
  font-size: 14px;
}

/* アクションボタンのスタイル */
.custom-select :deep(.vs__actions) {
  padding: 0;
}

/* ドロップダウンオプションのスタイル */
.custom-select :deep(.vs__dropdown-option) {
  font-size: 14px;
}

/* ホバー時のオプションのスタイル */
.custom-select :deep(.vs__dropdown-option--highlight) {
  background: var(--bs-primary);
  color: white;
}

/* ドロップダウン矢印のスタイル */
.custom-select :deep(.vs__open-indicator) {
  background-image: url(@/assets/images/icons/group-blue.svg);
  background-repeat: no-repeat;
  background-position: center;
  width: 20px;
  height: 20px;
  transform: none;
  transition: transform 0.15s;
  border: none;
  fill: transparent;
}

/* ドロップダウン矢印の疑似要素を非表示 */
.custom-select :deep(.vs__open-indicator::before) {
  display: none;
}

/* ドロップダウンが開いている時の矢印のスタイル */
.custom-select :deep(.vs--open .vs__open-indicator) {
  transform: rotate(180deg);
}

/* SVGアイコンを非表示 */
.custom-select :deep(.vs__open-indicator svg) {
  display: none;
}
</style>
